import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { BonusApplication } from "../../../../services/bonus/types";

interface ViewApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  application: BonusApplication | null;
}

const ViewApplicationDialog: React.FC<ViewApplicationDialogProps> = ({
  open,
  onClose,
  application,
}) => {
  const navigate = useNavigate();

  if (!application) return null;

  const handleViewDetails = () => {
    const { applicationType, applicationId } = application;

    // Map application types to their routes
    const routeMap: Record<string, string> = {
      CCM1: `/CCM/${applicationId}`,
      CCM2: `/CCM/${applicationId}`,
      CCM3: `/CCM/${applicationId}`,
      CCM3Manager: `/DCCM/${applicationId}`,
      CM3: `/CM3PM/${applicationId}`,
      PM: `/CM3PM/${applicationId}`,
      DCCM: `/DCCM/${applicationId}`,
      SPM: `/SPM/${applicationId}`,
    };

    const route = routeMap[applicationType];
    
    if (route) {
      navigate(route);
      onClose();
    } else {
      console.error(`No route found for application type: ${applicationType}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: "rounded-lg",
      }}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <DialogTitle className="text-xl font-semibold text-gray-900 p-0">
          View Application Details
        </DialogTitle>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>
      <DialogContent className="p-6">
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Application ID: <span className="font-medium text-gray-900">{application.applicationId}</span>
            </p>
            <p className="text-sm text-gray-600 mb-2">
              Type: <span className="font-medium text-gray-900">{application.applicationType}</span>
            </p>
            <p className="text-sm text-gray-600 mb-2">
              Status: <span className="font-medium text-gray-900">{application.status?.replace(/_/g, ' ') || 'N/A'}</span>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Applicant: <span className="font-medium text-gray-900">{application.employeeName || application.submittedBy}</span>
            </p>
          </div>

          <button
            onClick={handleViewDetails}
            className="w-full px-4 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors font-medium"
          >
            View Full Application Details
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewApplicationDialog;